/**
 * Presets gráficos (GDD, seção 9; item 3.3). Baixo tira as sombras em tempo real (fica uma sombra-bolha sob
 * cada atleta), congela a torcida e renderiza a 75% da resolução. O jogo mede os primeiros 10 s e sugere um preset.
 */
export type GraphicsPreset = "baixo" | "medio" | "alto";

export interface GraphicsSettings {
  label: string;
  /** Fração da resolução da tela usada para renderizar (o navegador amplia o resto). */
  resolutionScale: number;
  /** Teto da densidade de pixels (telas retina pedem 2 ou 3). */
  maxPixelRatio: number;
  antialias: boolean;
  /** Mapa de sombra em tempo real, em px; 0 desliga e usa a sombra-bolha. */
  shadowMapSize: number;
  /** Textura da sombra pré-calculada do estádio, em px. */
  bakeSize: number;
  animatedCrowd: boolean;
  /** Fração dos lugares ocupados. */
  crowdOccupancy: number;
  trees: number;
  /** Multiplica as distâncias de troca de LOD dos atletas (menor = troca mais cedo). */
  lodDistanceScale: number;
  /** Contorno dos atletas, em px numa tela de 1080 linhas. */
  outlinePx: number;
}

export const GRAPHICS_PRESET_ORDER: readonly GraphicsPreset[] = ["baixo", "medio", "alto"];

export const GRAPHICS_PRESETS: Readonly<Record<GraphicsPreset, GraphicsSettings>> = {
  baixo: {
    label: "Baixo",
    resolutionScale: 0.75,
    maxPixelRatio: 1,
    antialias: false,
    shadowMapSize: 0,
    bakeSize: 1024,
    animatedCrowd: false,
    crowdOccupancy: 0.6,
    trees: 120,
    lodDistanceScale: 0.6,
    outlinePx: 1.6,
  },
  medio: {
    label: "Médio",
    resolutionScale: 1,
    maxPixelRatio: 1.25,
    antialias: true,
    shadowMapSize: 1024,
    bakeSize: 2048,
    animatedCrowd: true,
    crowdOccupancy: 0.8,
    trees: 200,
    lodDistanceScale: 1,
    outlinePx: 1.8,
  },
  alto: {
    label: "Alto",
    resolutionScale: 1,
    maxPixelRatio: 2,
    antialias: true,
    shadowMapSize: 2048,
    bakeSize: 2048,
    animatedCrowd: true,
    crowdOccupancy: 0.88,
    trees: 280,
    lodDistanceScale: 1,
    outlinePx: 1.8,
  },
};

/** Meta de fps e piso aceitável (preset Baixo). */
export const FPS_TARGET = 60;
export const FPS_FLOOR = 30;
/** Abaixo desta média o jogo sugere descer um preset. */
export const FPS_STEP_DOWN = 50;
/** Acima desta média (e com o 1% mais lento acima de FPS_STEP_UP_LOW) sugere subir um preset. */
export const FPS_STEP_UP = 58;
export const FPS_STEP_UP_LOW = 45;

/** Preset sugerido depois de medir `avgFps` (média) e `low1Fps` (1% mais lentos) no preset `current`. */
export function suggestPreset(current: GraphicsPreset, avgFps: number, low1Fps: number): GraphicsPreset {
  const i = GRAPHICS_PRESET_ORDER.indexOf(current);
  if (avgFps < FPS_STEP_DOWN && i > 0) {
    // Muito abaixo do piso: desce dois de uma vez.
    const steps = avgFps < FPS_FLOOR ? 2 : 1;
    return GRAPHICS_PRESET_ORDER[Math.max(0, i - steps)] ?? "baixo";
  }
  if (avgFps >= FPS_STEP_UP && low1Fps >= FPS_STEP_UP_LOW && i < GRAPHICS_PRESET_ORDER.length - 1)
    return GRAPHICS_PRESET_ORDER[i + 1] ?? current;
  return current;
}
